import {Resource, ResourceProps} from 'react-admin';
import {observer} from 'mobx-react-lite';
import {Menu} from '@/services/manageService/menuService';
import {filterMenuForSidebar} from '@/utils/menuUtils';
import {useAuthMenu} from '@/services/authService/authMenu';
import {attachComponent, toRealmResourceName} from '@/router/attactComponent';

/**
 * Build dynamic Resource list; Resource.name === cleaned realm_url (full path).
 */
export const getDynamicResources = (menuTree: Menu[] | undefined | null): ResourceProps[] => {
    if (!menuTree || !Array.isArray(menuTree)) return [];
    try {
        const filteredMenus = filterMenuForSidebar(menuTree);
        const menusWithComponents = attachComponent(filteredMenus);
        const out: ResourceProps[] = [];
        const usedNames = new Set<string>();
        const walk = (nodes: Menu[]) => {
            nodes.forEach((node: Menu) => {
                // Folder nodes: recurse only; do not register empty Resource
                if (!node.list) {
                    if (node.children?.length) {
                        walk(node.children);
                    }
                    return;
                }

                const name = toRealmResourceName(
                    node.realmUrl,
                    node.realm,
                    node.menuId,
                );
                if (!name || usedNames.has(name)) {
                    if (node.children?.length) {
                        walk(node.children);
                    }
                    return;
                }

                out.push({
                    name,
                    options: {label: node.menuName},
                    list: node.list,
                });
                usedNames.add(name);
                if (node.children?.length) {
                    walk(node.children);
                }
            });
        };
        walk(menusWithComponents);
        return out;
    } catch {
        return [];
    }
};

const DynamicResources = observer(() => {
    const {data: menuTree = []} = useAuthMenu();
    const resources = getDynamicResources(menuTree);
    return (
        <>
            {resources.map(resource => (
                <Resource
                    key={String(resource.name)}
                    name={resource.name}
                    list={resource.list}
                    create={resource.create}
                    edit={resource.edit}
                    show={resource.show}
                    options={resource.options}
                />
            ))}
        </>
    );
});

export default DynamicResources;
